import React, { useCallback, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { uploadData, getUrl } from 'aws-amplify/storage'
import dayjs from 'dayjs'
import Button from '../../Buttons'
import CustomDatePicker from '../../Input/CustomDatePicker'
import CloseIcon from '../../../../assets/icons/actions/close.svg'
import { CREATE_EVENT } from '../../../../apollo/mutations/event'
import { useUserContext } from '../../../../context/UserContext'
import { usePlaceAutocomplete } from '../../../../hooks/usePlaceAutocomplete'
import { fetchTimezoneIdFromLatLng } from '../../../../utils/fetchTimezoneIdFromLatLng'
import { EVENT_TYPE_OTHER, EVENT_TYPE_OPTIONS, resolveEventType } from '../../../../constants/eventTypes'

// Daily booking window only — no weekly recurrence. Events run for a specific
// date range (startDate..endDate) and offer the same daily window each day.

// Form/backend use ISO YYYY-MM-DD; CustomDatePicker emits MM-DD-YYYY.
const isoToPickerDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return ''
  return `${m}-${d}-${y}`
}
const pickerToIsoDate = (mdy) => {
  if (!mdy) return ''
  const [m, d, y] = mdy.split('-')
  if (!y || !m || !d) return ''
  return `${y}-${m}-${d}`
}

// Inclusive list of YYYY-MM-DD strings between two ISO dates.
const enumerateDates = (startIso, endIso) => {
  if (!startIso || !endIso || endIso < startIso) return []
  const out = []
  const start = new Date(startIso + 'T00:00:00.000Z')
  const end = new Date(endIso + 'T00:00:00.000Z')
  for (let d = start.getTime(); d <= end.getTime(); d += 24 * 60 * 60 * 1000) {
    out.push(new Date(d).toISOString().slice(0, 10))
  }
  return out
}
const formatDateLabel = (iso) => {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00.000Z')
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function CreateEventModal({ onClose }) {
  const { openMessageModal, userData } = useUserContext()
  const modalRef = useRef(null)
  const imageRef = useRef(null)
  const addressInputRef = useRef(null)

  const [createEvent, { loading }] = useMutation(CREATE_EVENT, {
    refetchQueries: ['listEvents'],
  })

  const [form, setForm] = useState({
    eventName: '',
    description: '',
    city: '',
    state: '',
    country: 'USA',
    eventLocation: '',
    startDate: '',
    endDate: '',
    durationMinutes: 15,
    bufferMinutes: 10,
    capacity: 1,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago',
    visibility: 'ENABLED',
    dailyStartTime: '09:00',
    dailyEndTime: '17:00',
    breakStartTime: '',
    breakEndTime: '',
    fullPaymentAtBooking: false,
  })
  const [dailySchedule, setDailySchedule] = useState([])
  const [eventTypeSelect, setEventTypeSelect] = useState('')
  const [eventTypeOther, setEventTypeOther] = useState('')
  const [hasSponsorship, setHasSponsorship] = useState(false)
  const [sponsorshipTitle, setSponsorshipTitle] = useState('')
  const [placeCoordinates, setPlaceCoordinates] = useState(null)
  const [imageUrl, setImageUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [timezoneLookupLoading, setTimezoneLookupLoading] = useState(false)
  const [timezoneFromPinError, setTimezoneFromPinError] = useState(null)
  const [showTimezoneOverride, setShowTimezoneOverride] = useState(false)

  const handlePlaceSelected = useCallback((data) => {
    if (!data) {
      setPlaceCoordinates(null)
      return
    }
    setPlaceCoordinates({ latitude: data.latitude, longitude: data.longitude })
    setForm((f) => ({
      ...f,
      city: data.city || f.city,
      state: data.state || f.state,
      country: data.country || f.country,
      eventLocation: data.eventLocation || f.eventLocation,
    }))
    const line = data.formattedAddress || data.eventLocation || ''
    queueMicrotask(() => {
      if (addressInputRef.current) addressInputRef.current.value = line
    })
    setShowTimezoneOverride(false)
    setTimezoneFromPinError(null)
    setTimezoneLookupLoading(true)
    fetchTimezoneIdFromLatLng(data.latitude, data.longitude)
      .then((tz) => {
        setForm((f) => ({ ...f, timezone: tz }))
        setTimezoneFromPinError(null)
      })
      .catch((err) => {
        setTimezoneFromPinError(err?.message || 'Could not load timezone for this location.')
      })
      .finally(() => {
        setTimezoneLookupLoading(false)
      })
  }, [])

  const { mapsLoading, mapsError } = usePlaceAutocomplete(addressInputRef, handlePlaceSelected)

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }))

  const handlerImage = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      setUploading(true)
      const result = await uploadData({
        key: `event/${dayjs().unix()}-${file.name}`,
        data: file,
        options: { accessLevel: 'guest' },
      }).result
      // Ask Amplify for the real public URL — avoids relying on a
      // VITE_S3_BUCKET env that may not be set in this environment.
      const { url } = await getUrl({
        key: result.key,
        options: { accessLevel: 'guest' },
      })
      // url is a URL object whose query string is a presigned signature; we
      // only want the canonical object URL so the value we send to the API is
      // stable and reusable.
      const u = new URL(url.toString())
      setImageUrl(`${u.origin}${u.pathname}`)
    } catch (err) {
      openMessageModal('Error', err?.message || 'Image upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!form.eventName || !form.eventLocation || !form.country) {
      openMessageModal('Heads Up', "We need a name, location & country to save this one.")
      return
    }
    if (hasSponsorship && !sponsorshipTitle.trim()) {
      openMessageModal('Heads Up', 'Add a sponsorship title, or turn off sponsorship.')
      return
    }
    if (!form.startDate || !form.endDate) {
      openMessageModal('Heads Up', 'Set the start & end dates for this event.')
      return
    }
    if (form.endDate < form.startDate) {
      openMessageModal('Heads Up', 'End date must be on or after the start date.')
      return
    }
    if (!form.dailyStartTime || !form.dailyEndTime || form.dailyEndTime <= form.dailyStartTime) {
      openMessageModal('Heads Up', 'Daily window end time must be after the start time.')
      return
    }
    if (
      (form.breakStartTime || form.breakEndTime) &&
      (!form.breakStartTime ||
        !form.breakEndTime ||
        form.breakEndTime <= form.breakStartTime ||
        form.breakStartTime < form.dailyStartTime ||
        form.breakEndTime > form.dailyEndTime)
    ) {
      openMessageModal('Heads Up', 'Break must sit inside the daily window and end after it starts.')
      return
    }
    for (const row of dailySchedule) {
      if (!row.startTime || !row.endTime || row.endTime <= row.startTime) {
        openMessageModal('Heads Up', `Fix the times for ${row.date} — close must be after open.`)
        return
      }
      if (
        (row.breakStartTime || row.breakEndTime) &&
        (!row.breakStartTime ||
          !row.breakEndTime ||
          row.breakEndTime <= row.breakStartTime ||
          row.breakStartTime < row.startTime ||
          row.breakEndTime > row.endTime)
      ) {
        openMessageModal('Heads Up', `Break for ${row.date} must sit inside that day's window.`)
        return
      }
    }
    if (!placeCoordinates) {
      openMessageModal(
        'Heads Up',
        'Choose an address from the suggestions so we can save the map location.',
      )
      return
    }
    if (!imageUrl) {
      openMessageModal('Heads Up', 'Please upload an event image first.')
      return
    }
    const resolvedType = resolveEventType(eventTypeSelect, eventTypeOther)
    if (eventTypeSelect === EVENT_TYPE_OTHER && !resolvedType) {
      openMessageModal('Heads Up', 'Enter a custom event type, or choose a different option.')
      return
    }
    try {
      await createEvent({
        variables: {
          input: {
            eventName: form.eventName,
            description: form.description || null,
            eventType: resolvedType,
            sponsorshipTitle: hasSponsorship ? sponsorshipTitle.trim() : null,
            // Local noon keeps the ISO timestamp on the intended calendar day
            // regardless of the admin's timezone (bare YYYY-MM-DD parses as UTC
            // midnight, which shifts a day backward in US timezones).
            date: form.startDate
              ? new Date(`${form.startDate}T12:00:00`).toISOString()
              : new Date(Date.now()).toISOString(),
            city: form.city || null,
            state: form.state || null,
            country: form.country,
            eventLocation: form.eventLocation,
            coordinates: {
              latitude: placeCoordinates.latitude,
              longitude: placeCoordinates.longitude,
            },
            image: imageUrl,
            profile: {
              name: userData?.firstName || 'SaddleFit',
              owner: userData?.email || 'saddlefit',
              type: 'admin',
            },
            visibility: form.visibility,
            startDate: form.startDate || null,
            endDate: form.endDate || null,
            durationMinutes: parseInt(form.durationMinutes, 10) || 15,
            bufferMinutes: Math.max(0, parseInt(form.bufferMinutes, 10) || 0),
            capacity: Math.max(1, parseInt(form.capacity, 10) || 1),
            timezone: form.timezone,
            dailyStartTime: form.dailyStartTime,
            dailyEndTime: form.dailyEndTime,
            breakStartTime: form.breakStartTime || null,
            breakEndTime: form.breakEndTime || null,
            dailySchedule: dailySchedule.length
              ? dailySchedule.map((r) => ({
                  date: r.date,
                  startTime: r.startTime,
                  endTime: r.endTime,
                  breakStartTime: r.breakStartTime || null,
                  breakEndTime: r.breakEndTime || null,
                }))
              : null,
            fullPaymentAtBooking: Boolean(form.fullPaymentAtBooking),
          },
        },
      })
      openMessageModal('You\'re All Set.', 'Event created.')
      onClose?.()
    } catch (err) {
      openMessageModal('Something Went Sideways', err?.message || 'Mind trying again?')
    }
  }

  const field =
    'w-full mt-2 px-4 h-[48px] rounded-[10px] border border-[#c3c9d4] text-[14px] font-montserrat'
  const label =
    'block text-[13px] font-montserrat font-semibold text-[#2b364b] tracking-wide mb-1.5'
  const hint = 'text-[12px] font-montserrat text-[#6b7280] mt-1.5 leading-relaxed'
  const sectionTitle =
    'text-lg font-montserrat font-semibold text-[#2b364b] mb-1'
  const sectionCard = 'rounded-2xl border border-[#e8eaef] bg-[#fafbfc] p-6 sm:p-8 space-y-6'

  return (
    <div>
      <div className="w-full h-full fixed top-0 left-0 z-10 bg-black opacity-60" />
      <div
        ref={modalRef}
        className="w-[min(96vw,1120px)] min-w-0 max-w-[1120px] rounded-[24px] bg-white shadow-2xl z-20 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-[min(94vh,920px)] overflow-y-auto px-5 py-8 sm:px-12 sm:py-10"
      >
        <div className="absolute top-5 right-5">
          <img
            src={CloseIcon}
            alt="Close"
            className="w-5 h-5 cursor-pointer"
            onClick={onClose}
          />
        </div>
        <h2 className="add-events !text-[36px] !mt-0 mb-8 pr-8">Create Event</h2>

        <form onSubmit={onSubmit} className="space-y-10">
          <div>
            <label className={label} htmlFor="create-event-name">Event name</label>
            <input id="create-event-name" className={field} value={form.eventName} onChange={set('eventName')} placeholder="Spring Fitting Clinic" />
          </div>

          <div>
            <label className={label} htmlFor="create-event-desc">Description</label>
            <textarea id="create-event-desc" className={field + ' !h-[100px] py-3'} value={form.description} onChange={set('description')} placeholder="Tell riders what to expect." />
          </div>

          <div>
            <label className={label} htmlFor="create-event-type">Event type</label>
            <select
              id="create-event-type"
              className={field}
              value={eventTypeSelect}
              onChange={(e) => setEventTypeSelect(e.target.value)}
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.label + opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {eventTypeSelect === EVENT_TYPE_OTHER && (
              <div className="mt-4">
                <label className={label} htmlFor="create-event-type-other">Describe event type</label>
                <input
                  id="create-event-type-other"
                  className={field}
                  value={eventTypeOther}
                  onChange={(e) => setEventTypeOther(e.target.value)}
                  placeholder="e.g. Trail clinic"
                />
              </div>
            )}
          </div>

          <div>
            <label className={label} htmlFor="create-event-visibility">Visibility</label>
            <select
              id="create-event-visibility"
              className={field}
              value={form.visibility}
              onChange={set('visibility')}
            >
              <option value="ENABLED">Enabled — visible on the public site</option>
              <option value="DISABLED">Disabled — hidden from the public site</option>
            </select>
            <p className={hint}>
              Disabled events are hidden from the public site — use it as a draft state until you're ready to go live.
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasSponsorship}
                onChange={(e) => {
                  setHasSponsorship(e.target.checked)
                  if (!e.target.checked) setSponsorshipTitle('')
                }}
                className="h-4 w-4 rounded border-[#c3c9d4]"
              />
              <span className={`${label} !mb-0`}>This event has a sponsor</span>
            </label>
            {hasSponsorship && (
              <div>
                <label className={label} htmlFor="create-event-sponsor-title">Sponsorship title</label>
                <input
                  id="create-event-sponsor-title"
                  className={field}
                  value={sponsorshipTitle}
                  onChange={(e) => setSponsorshipTitle(e.target.value)}
                  placeholder="Shown with the event (e.g. sponsor name)"
                />
              </div>
            )}
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Where</h3>
            <div>
              <label className={label} htmlFor="create-event-address-search">Search address or venue</label>
              <input
                id="create-event-address-search"
                ref={addressInputRef}
                className={field}
                onChange={() => setPlaceCoordinates(null)}
                placeholder="Start typing an address or place name…"
                autoComplete="off"
              />
              <p className={hint}>
                Select a suggestion to set the map pin and fill city, state, and country.
              </p>
              {mapsLoading && (
                <p className="text-[12px] font-montserrat text-[#5c80b6] mt-2">Loading address search…</p>
              )}
              {mapsError && (
                <p className="text-[12px] font-montserrat text-[#f25022] mt-2" role="alert">
                  {mapsError}
                </p>
              )}
            </div>

            <div className="space-y-5">
              <div>
                <label className={label} htmlFor="create-event-city">City</label>
                <input id="create-event-city" className={field} value={form.city} onChange={set('city')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                <div>
                  <label className={label} htmlFor="create-event-state">State / province</label>
                  <input id="create-event-state" className={field} value={form.state} onChange={set('state')} />
                </div>
                <div>
                  <label className={label} htmlFor="create-event-country">Country</label>
                  <input id="create-event-country" className={field} value={form.country} onChange={set('country')} />
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-[#e8eaef] bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`${label} !mb-0`}>Timezone</span>
                {timezoneLookupLoading && (
                  <span className="text-[12px] font-montserrat text-[#5c80b6]">Looking up…</span>
                )}
              </div>
              {!showTimezoneOverride ? (
                <>
                  <p className={`${hint} !mt-0`}>
                    <strong className="font-semibold text-[#374151]">{form.timezone}</strong>
                    {' '}— set from the map pin when you choose an address above.
                  </p>
                  <button
                    type="button"
                    className="text-[13px] font-montserrat font-semibold text-[#5c80b6] underline"
                    onClick={() => setShowTimezoneOverride(true)}
                  >
                    Override timezone
                  </button>
                </>
              ) : (
                <>
                  <label className={label} htmlFor="create-event-tz">IANA timezone</label>
                  <input
                    id="create-event-tz"
                    className={field + ' max-w-xl'}
                    value={form.timezone}
                    onChange={set('timezone')}
                  />
                  <button
                    type="button"
                    className="text-[13px] font-montserrat text-[#6b7280] underline"
                    onClick={() => {
                      setShowTimezoneOverride(false)
                      if (placeCoordinates) {
                        setTimezoneFromPinError(null)
                        setTimezoneLookupLoading(true)
                        fetchTimezoneIdFromLatLng(
                          placeCoordinates.latitude,
                          placeCoordinates.longitude,
                        )
                          .then((tz) => {
                            setForm((f) => ({ ...f, timezone: tz }))
                            setTimezoneFromPinError(null)
                          })
                          .catch((err) => {
                            setTimezoneFromPinError(
                              err?.message || 'Could not load timezone for this location.',
                            )
                          })
                          .finally(() => setTimezoneLookupLoading(false))
                      }
                    }}
                  >
                    Use pin-based timezone
                  </button>
                </>
              )}
              {timezoneFromPinError && (
                <p className="text-[12px] font-montserrat text-[#b45309] mt-1" role="status">
                  {timezoneFromPinError} You can override the timezone below or fix your Google Cloud Time Zone API setup.
                </p>
              )}
            </div>

            <div>
              <label className={label} htmlFor="create-event-location-label">Location shown on site</label>
              <input
                id="create-event-location-label"
                className={field}
                value={form.eventLocation}
                onChange={set('eventLocation')}
                placeholder="Filled when you pick a place above — you can edit"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <label className={label} htmlFor="create-event-start">Start date</label>
              <CustomDatePicker
                inputId="create-event-start"
                value={isoToPickerDate(form.startDate)}
                onChange={(mdy) =>
                  setForm((f) => ({ ...f, startDate: pickerToIsoDate(mdy) }))
                }
                placeholder="MM-DD-YYYY"
                clearable
                floatingLabel={false}
                className="w-full"
                inputClassName={field}
              />
            </div>
            <div>
              <label className={label} htmlFor="create-event-end">End date</label>
              <CustomDatePicker
                inputId="create-event-end"
                value={isoToPickerDate(form.endDate)}
                onChange={(mdy) =>
                  setForm((f) => ({ ...f, endDate: pickerToIsoDate(mdy) }))
                }
                placeholder="MM-DD-YYYY"
                clearable
                floatingLabel={false}
                className="w-full"
                inputClassName={field}
                isValidDate={(current) =>
                  !form.startDate ||
                  current.isSameOrAfter(
                    // moment parses YYYY-MM-DD natively
                    new Date(form.startDate + 'T00:00:00'),
                    'day',
                  )
                }
              />
            </div>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Booking &amp; sessions</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Each booking is one start time. <strong className="font-semibold text-[#374151]">Session length</strong> is how
              long the appointment runs for the customer. <strong className="font-semibold text-[#374151]">Buffer</strong> is extra time
              after that before the next slot can start (cleanup, travel, notes).
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10">
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-duration">Session length</label>
                <input id="create-event-duration" type="number" min="15" step="15" className={field} value={form.durationMinutes} onChange={set('durationMinutes')} />
                <p className={hint}>Minutes per appointment.</p>
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-buffer">Buffer after session</label>
                <input
                  id="create-event-buffer"
                  type="number"
                  min="0"
                  step="5"
                  className={field}
                  value={form.bufferMinutes}
                  onChange={set('bufferMinutes')}
                />
                <p className={hint}>Minutes before the next start time. Use 0 for back-to-back starts.</p>
              </div>
              <div className="min-w-0 sm:col-span-2">
                <label className={label} htmlFor="create-event-capacity">
                  Concurrent bookings per slot
                </label>
                <input
                  id="create-event-capacity"
                  type="number"
                  min="1"
                  step="1"
                  className={field + ' max-w-xs'}
                  value={form.capacity}
                  onChange={set('capacity')}
                />
                <p className={hint}>Usually 1 — increase only if your workflow allows more than one booking at the same time.</p>
              </div>
            </div>
            <label className="flex items-start gap-3 mt-6 cursor-pointer max-w-3xl">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 rounded border-[#c3c9d4] text-[#2b364b] focus:ring-[#5c80b6]"
                checked={Boolean(form.fullPaymentAtBooking)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, fullPaymentAtBooking: e.target.checked }))
                }
              />
              <span className={`${hint} !mt-0`}>
                <span className="font-semibold text-[#374151]">On-site / pay in full at booking.</span>{' '}
                Charge the full scan price (from Settings) in one checkout instead of a $50 deposit plus balance later.
              </span>
            </label>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Daily booking window</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Bookable times use the same window every day between the start &amp; end dates above. Offered times step every{' '}
              <strong className="font-semibold text-[#374151]">session length + buffer</strong> from window open to window close.
              Add a break (e.g. lunch) and no slots will be offered during that time.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-daily-start">Window opens</label>
                <input
                  id="create-event-daily-start"
                  type="time"
                  className={field}
                  value={form.dailyStartTime}
                  onChange={set('dailyStartTime')}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-daily-end">Window closes</label>
                <input
                  id="create-event-daily-end"
                  type="time"
                  className={field}
                  value={form.dailyEndTime}
                  onChange={set('dailyEndTime')}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-break-start">Break starts (optional)</label>
                <input
                  id="create-event-break-start"
                  type="time"
                  className={field}
                  value={form.breakStartTime}
                  onChange={set('breakStartTime')}
                />
              </div>
              <div className="min-w-0">
                <label className={label} htmlFor="create-event-break-end">Break ends (optional)</label>
                <input
                  id="create-event-break-end"
                  type="time"
                  className={field}
                  value={form.breakEndTime}
                  onChange={set('breakEndTime')}
                />
              </div>
            </div>
          </div>

          <div className={sectionCard}>
            <h3 className={sectionTitle}>Per-day overrides (optional)</h3>
            <p className={`${hint} !mt-0 max-w-3xl`}>
              Need a different schedule on specific days? Add overrides for any day in the range and those will replace
              the daily defaults above. Days you don't list keep using the defaults.
            </p>

            {form.startDate && form.endDate && form.endDate >= form.startDate ? (
              (() => {
                const allDates = enumerateDates(form.startDate, form.endDate)
                const usedDates = new Set(dailySchedule.map((r) => r.date))
                const availableDates = allDates.filter((d) => !usedDates.has(d))
                return (
                  <>
                    {(() => {
                      const compactField =
                        'w-full px-2 h-[36px] rounded-[8px] border border-[#c3c9d4] text-[13px] font-montserrat'
                      const updateRow = (date, k, v) =>
                        setDailySchedule((rows) =>
                          rows.map((r) => (r.date === date ? { ...r, [k]: v } : r)),
                        )
                      return (
                        <>
                          {dailySchedule.length > 0 && (
                            <div className="hidden lg:grid grid-cols-[minmax(120px,150px)_repeat(4,minmax(80px,1fr))_28px] gap-2 items-end px-3 text-[11px] font-montserrat font-semibold text-[#6b7280] uppercase tracking-wide">
                              <div>Day</div>
                              <div>Opens</div>
                              <div>Closes</div>
                              <div>Break starts</div>
                              <div>Break ends</div>
                              <div />
                            </div>
                          )}
                          {dailySchedule.length > 0 && (
                            <div className="space-y-2">
                              {dailySchedule
                                .slice()
                                .sort((a, b) => a.date.localeCompare(b.date))
                                .map((row) => (
                                  <div
                                    key={row.date}
                                    className="grid grid-cols-2 lg:grid-cols-[minmax(120px,150px)_repeat(4,minmax(80px,1fr))_28px] gap-2 items-center px-3 py-2 rounded-lg border border-[#e8eaef] bg-white"
                                  >
                                    <div className="col-span-2 lg:col-span-1 text-[13px] font-semibold text-[#2b364b]">
                                      {formatDateLabel(row.date)}
                                    </div>
                                    <input
                                      type="time"
                                      aria-label={`Opens on ${row.date}`}
                                      className={compactField}
                                      value={row.startTime}
                                      onChange={(e) => updateRow(row.date, 'startTime', e.target.value)}
                                    />
                                    <input
                                      type="time"
                                      aria-label={`Closes on ${row.date}`}
                                      className={compactField}
                                      value={row.endTime}
                                      onChange={(e) => updateRow(row.date, 'endTime', e.target.value)}
                                    />
                                    <input
                                      type="time"
                                      aria-label={`Break starts on ${row.date}`}
                                      className={compactField}
                                      value={row.breakStartTime}
                                      onChange={(e) => updateRow(row.date, 'breakStartTime', e.target.value)}
                                    />
                                    <input
                                      type="time"
                                      aria-label={`Break ends on ${row.date}`}
                                      className={compactField}
                                      value={row.breakEndTime}
                                      onChange={(e) => updateRow(row.date, 'breakEndTime', e.target.value)}
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setDailySchedule((rows) => rows.filter((r) => r.date !== row.date))
                                      }
                                      aria-label={`Remove override for ${row.date}`}
                                      className="justify-self-end h-7 w-7 rounded-full text-[#f25022] hover:bg-red-50 text-lg leading-none"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}

                          <div className="flex items-end gap-3 max-w-md">
                            <select
                              id="create-event-add-override"
                              aria-label="Add override for"
                              className={compactField}
                              value=""
                              onChange={(e) => {
                                const date = e.target.value
                                if (!date) return
                                setDailySchedule((rows) => [
                                  ...rows,
                                  {
                                    date,
                                    startTime: form.dailyStartTime,
                                    endTime: form.dailyEndTime,
                                    breakStartTime: form.breakStartTime || '',
                                    breakEndTime: form.breakEndTime || '',
                                  },
                                ])
                              }}
                            >
                              <option value="">
                                {availableDates.length ? '+ Add override for…' : 'No more days in range'}
                              </option>
                              {availableDates.map((d) => (
                                <option key={d} value={d}>
                                  {formatDateLabel(d)} ({d})
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )
                    })()}
                  </>
                )
              })()
            ) : (
              <p className={`${hint} !mt-0`}>Pick a start &amp; end date above to add per-day overrides.</p>
            )}
          </div>

          <div>
            <label className={label}>Event image</label>
            <input
              type="file"
              accept="image/*"
              ref={imageRef}
              onChange={handlerImage}
              className="sr-only"
              id="create-event-image-input"
            />
            <div className="flex items-center gap-4 mt-2">
              {imageUrl && !uploading ? (
                <img
                  src={imageUrl}
                  alt="Event preview"
                  className="w-[88px] h-[88px] object-cover rounded-[10px] border border-[#e8eaef]"
                />
              ) : (
                <div className="w-[88px] h-[88px] rounded-[10px] border border-dashed border-[#c3c9d4] bg-[#fafbfc] flex items-center justify-center text-[11px] font-montserrat text-[#9ca3af]">
                  {uploading ? '…' : 'No image'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => imageRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center justify-center h-[44px] px-5 rounded-full bg-[#2b364b] text-white text-[13px] font-montserrat font-semibold tracking-wide hover:opacity-90 disabled:opacity-60 transition"
                >
                  {uploading ? 'Uploading…' : imageUrl ? 'Replace image' : 'Choose image'}
                </button>
                <p className={`${hint} !mt-2`}>PNG, JPG or JPEG · up to 5 MB</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-4 sm:gap-5 pt-4 justify-center items-stretch sm:items-center">
            <Button
              type="button"
              onClick={onClose}
              className="!border-[#2b364b] w-full sm:w-[min(100%,200px)] cursor-pointer h-[56px] rounded-[56px]"
              primaryOutline
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="text-white cursor-pointer w-full sm:w-[min(100%,200px)] h-[56px] rounded-[56px]"
              primary
            >
              Save Event
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateEventModal
